export const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  TL: "team_leader",
  ADVISOR: "advisor",
  QUALITY: "quality",
};

export const ROLE_LABELS = {
  admin: "Admin",
  manager: "Manager",
  team_leader: "Team Leader",
  advisor: "Advisor",
  quality: "Quality",
};

export const isAdmin = (user) => user?.userRole === "admin" || user?.userIsAdmin === true;
export const isSuperAdmin = (user) => user?.userIsSuperAdmin === true;
export const isManager = (user) => user?.userRole === "manager";
export const isTL = (user) => user?.userRole === "team_leader";
export const isAdvisor = (user) => user?.userRole === "advisor";
export const isQuality = (user) => user?.userRole === "quality";
export const isSupervisory = (user) => isAdmin(user) || isManager(user) || isTL(user);
export const canMakeCalls = (user) => isAdvisor(user) || hasPermission(user, "startCallAccess");

/**
 * Check if a navigation tab/page should be visible to this user.
 * Reads `permissions.<feature>.tabVisibility`.
 *
 *   isTabVisible(user, 'leads')   → permissions.leads.tabVisibility === 'allowed'
 *   isTabVisible(user, 'rtm')     → permissions.rtm.tabVisibility === 'allowed'
 */
export const isTabVisible = (user, feature) => {
  const val = user?.permissions?.[feature];
  if (!val) return false;
  if (typeof val === "string") return val === "allowed";
  return val?.tabVisibility === "allowed";
};

/**
 * Check a specific permission — supports flat and dot-notation.
 *
 *   hasPermission(user, 'startCallAccess')          → flat string check
 *   hasPermission(user, 'reports.allocationReport')  → nested dot-notation
 *   hasPermission(user, 'reports')                   → ANY functional sub-key (excl. tabVisibility)
 */
export const hasPermission = (user, feature) => {
  const perms = user?.permissions;
  if (!perms) return false;

  const parts = feature.split(".");

  if (parts.length === 2) {
    return perms[parts[0]]?.[parts[1]] === "allowed";
  }

  const val = perms[parts[0]];
  if (typeof val === "string") return val === "allowed";

  if (val && typeof val === "object") {
    return Object.entries(val).some(
      ([k, v]) => k !== "tabVisibility" && v === "allowed"
    );
  }
  return false;
};

export const getRoleLabel = (user) => {
  if (!user?.userRole) return user?.userIsAdmin ? "Admin" : "Advisor";
  return ROLE_LABELS[user.userRole] || user.userRole;
};

export const getDefaultPath = (user) => {
  if (isAdmin(user)) return "/leads";
  if (isManager(user)) return "/leads";
  if (isTL(user)) return "/leads";
  if (isQuality(user)) return "/leads";
  return "/my-leads";
};

/**
 * Has the superadmin switched this module on for the user's organisation?
 *
 * Separate from hasPermission: that answers "may this ROLE use the module",
 * which the organisation's own admin controls. This answers "does the
 * organisation HAVE the module at all", which only a superadmin sets. Both must
 * pass for a feature to be usable.
 *
 * Display only — every endpoint re-checks server-side. Absent means enabled, so
 * sessions that predate orgFeatures keep working; the WABA Talk integrations are
 * the exception and are off until switched on, which the backend decides and
 * sends here as an explicit false.
 */
export const isOrgFeatureEnabled = (user, feature) => {
  const features = user?.orgFeatures;
  if (!features) return true;
  const value = features[String(feature).split(".")[0]];
  return value === undefined ? true : value === true;
};

/** Can this user open a WABA Talk WhatsApp chat from a lead? */
export const canUseWhatsApp = (user) => isOrgFeatureEnabled(user, "whatsapp");

/** Can this user send a WABA Talk SMS from a lead? */
export const canUseSms = (user) => isOrgFeatureEnabled(user, "sms");

/** Agent Assist ("Continue Customer"): opt-in per organisation, never for quality or superadmin.
 *  Cosmetic — the backend decides per lead (ownership, org, feature, configuration). */
export const canUseAgentAssist = (user) =>
  user?.orgFeatures?.agentAssist === true &&
  user?.userIsSuperAdmin !== true &&
  ["advisor", "team_leader", "manager", "admin"].includes(user?.userRole || (user?.userIsAdmin ? "admin" : "advisor"));
